/**
 * A composable for managing session storage with expiration.
 * Items set in session storage will expire after the specified TTL (in minutes).
 *
 * Example usage:
 * const { set, get, remove } = useExpiringSessionStorage();
 * set("key", value, ttlMinutes);
 * get("key", defaultValue);
 *
 * This also takes care of typing and JSON serialization/deserialization.
 */
export const useExpiringSessionStorage = () => {
    const set = (key, value, ttlMinutes = 30) => {
        const now = Date.now();
        const item = {
            value,
            expiry: now + ttlMinutes * 60 * 1000
        };
        sessionStorage.setItem(key, JSON.stringify(item));
    };
    const get = (key, defaultVal = null) => {
        const itemStr = sessionStorage.getItem(key);
        if (!itemStr)
            return defaultVal;
        try {
            const item = JSON.parse(itemStr);
            const now = Date.now();
            if (now > item.expiry) {
                sessionStorage.removeItem(key);
                return defaultVal;
            }
            return item.value;
        }
        catch (e) {
            console.error("Error parsing session storage item", e);
            sessionStorage.removeItem(key);
            return defaultVal;
        }
    };
    const remove = (key) => {
        sessionStorage.removeItem(key);
    };
    return { set, get, remove };
};
